import React, { useEffect, useRef } from "react";
import { Alert, Grid, InputLabel, MenuItem, OutlinedInput, Select } from "@mui/material";
import { useEmendasParlamentaresState } from "../context/emendas-parlamentares-context";
import { useEmendasParlamentaresController } from "../context/emendas-parlamentares-controller";
import PlainTextArea from "../../../components/plain-text-area";
import { SelectWithQuickAdd } from "../../../components/select-with-quick-add";
import { TextInputWithCounter } from "../../../components/text-input-with-counter";
import { CurrencyInput } from "../../../components/currency-input";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";

export const EmendaParlamentarForm = () => {
    const { dataRequest, orgaosList, tiposList, formasRepasseList, funcoesGovernoList, conveniosList } = useEmendasParlamentaresState();
    const { handleOnChangeForm, loadLookups } = useEmendasParlamentaresController();
    const firstInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
        (async () => {
            await loadLookups();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            <Grid container spacing={2}>
                <Grid item lg={12} sm={12} xl={12}>
                    <Alert severity="info">
                        Se Órgão ou Convênio ainda não existirem, cadastre-os primeiro no menu lateral. Modalidade da Emenda, Forma de Repasse e Função de Governo podem ser adicionadas na hora pelo botão "+" ao lado de cada campo.
                    </Alert>
                </Grid>

                <Grid item lg={6} sm={6} xl={6}>
                    <InputLabel>Órgão *</InputLabel>
                    <Select
                        size="small"
                        error={!dataRequest?.idOrgao}
                        value={dataRequest?.idOrgao ?? ''}
                        onChange={(e) => handleOnChangeForm('idOrgao', e.target.value)}
                        fullWidth
                    >
                        <MenuItem value={''} key={0}><em>Selecionar</em></MenuItem>
                        {orgaosList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                    </Select>
                </Grid>

                <Grid item lg={6} sm={6} xl={6}>
                    <InputLabel>Número da Emenda *</InputLabel>
                    <TextInputWithCounter
                        ref={firstInputRef}
                        size="small"
                        maxLength={20}
                        error={!dataRequest?.numeroEmenda}
                        value={dataRequest?.numeroEmenda || ''}
                        onChange={(e) => handleOnChangeForm('numeroEmenda', e.target.value)}
                        fullWidth
                    />
                </Grid>

                <Grid item lg={4} sm={4} xl={4}>
                    <SelectWithQuickAdd
                        label="Modalidade da Emenda *"
                        error={!dataRequest?.idTipoEmenda}
                        value={dataRequest?.idTipoEmenda ?? ''}
                        onChange={(value) => handleOnChangeForm('idTipoEmenda', value)}
                        options={tiposList}
                        addLabel="Nova Modalidade da Emenda"
                        onAdd={async (nome) => {
                            await AdminPortalAppService.addUpdateTipoEmenda({ nomTipoEmenda: nome });
                            await loadLookups();
                        }}
                    />
                </Grid>

                <Grid item lg={4} sm={4} xl={4}>
                    <SelectWithQuickAdd
                        label="Forma de Repasse *"
                        error={!dataRequest?.idFormaRepasseEmenda}
                        value={dataRequest?.idFormaRepasseEmenda ?? ''}
                        onChange={(value) => handleOnChangeForm('idFormaRepasseEmenda', value)}
                        options={formasRepasseList}
                        addLabel="Nova Forma de Repasse"
                        onAdd={async (nome) => {
                            await AdminPortalAppService.addUpdateFormaRepasseEmenda({ nomFormaRepasseEmenda: nome });
                            await loadLookups();
                        }}
                    />
                </Grid>

                <Grid item lg={4} sm={4} xl={4}>
                    <SelectWithQuickAdd
                        label="Função de Governo *"
                        error={!dataRequest?.idFuncaoGoverno}
                        value={dataRequest?.idFuncaoGoverno ?? ''}
                        onChange={(value) => handleOnChangeForm('idFuncaoGoverno', value)}
                        options={funcoesGovernoList}
                        addLabel="Nova Função de Governo"
                        onAdd={async (nome) => {
                            await AdminPortalAppService.addUpdateFuncaoGoverno({ nomFuncaoGoverno: nome });
                            await loadLookups();
                        }}
                    />
                </Grid>

                <Grid item lg={4} sm={4} xl={4}>
                    <InputLabel>Autor *</InputLabel>
                    <TextInputWithCounter
                        size="small"
                        maxLength={150}
                        error={!dataRequest?.nomAutor}
                        value={dataRequest?.nomAutor || ''}
                        onChange={(e) => handleOnChangeForm('nomAutor', e.target.value)}
                        fullWidth
                    />
                </Grid>

                <Grid item lg={4} sm={4} xl={4}>
                    <InputLabel>Tipo de Origem *</InputLabel>
                    <TextInputWithCounter
                        size="small"
                        maxLength={20}
                        error={!dataRequest?.tipoOrigem}
                        value={dataRequest?.tipoOrigem || ''}
                        onChange={(e) => handleOnChangeForm('tipoOrigem', e.target.value)}
                        fullWidth
                    />
                </Grid>

                <Grid item lg={4} sm={4} xl={4}>
                    <InputLabel>Exercício *</InputLabel>
                    <OutlinedInput
                        size="small"
                        type="number"
                        error={!dataRequest?.ano}
                        value={dataRequest?.ano ?? ''}
                        onChange={(e) => handleOnChangeForm('ano', Number(e.target.value))}
                        fullWidth
                    />
                </Grid>

                <Grid item lg={6} sm={6} xl={6}>
                    <InputLabel>Convênio (opcional)</InputLabel>
                    <Select
                        size="small"
                        value={dataRequest?.idConvenio ?? ''}
                        onChange={(e) => handleOnChangeForm('idConvenio', e.target.value === '' ? null : e.target.value)}
                        fullWidth
                    >
                        <MenuItem value={''} key={0}><em>Nenhum</em></MenuItem>
                        {conveniosList?.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((o) => <MenuItem value={o.id} key={o.id}>{o.name}</MenuItem>)}
                    </Select>
                </Grid>

                <Grid item lg={12} sm={12} xl={12}>
                    <InputLabel>Objeto *</InputLabel>
                    <PlainTextArea
                        size="small"
                        rows={4}
                        error={!dataRequest?.textoObjetoEmenda}
                        value={dataRequest?.textoObjetoEmenda || ''}
                        onChange={(e: any) => handleOnChangeForm('textoObjetoEmenda', e.target.value)}
                        fullWidth
                    />
                </Grid>

                <Grid item lg={6} sm={6} xl={6}>
                    <InputLabel>Valor Previsto *</InputLabel>
                    <CurrencyInput
                        size="small"
                        error={!dataRequest?.valorPrevisto || dataRequest.valorPrevisto <= 0}
                        value={dataRequest?.valorPrevisto}
                        onChange={(value) => handleOnChangeForm('valorPrevisto', value)}
                        fullWidth
                    />
                </Grid>

                <Grid item lg={6} sm={6} xl={6}>
                    <InputLabel>Valor Realizado *</InputLabel>
                    <CurrencyInput
                        size="small"
                        error={!dataRequest?.valorRealizado || dataRequest.valorRealizado <= 0}
                        value={dataRequest?.valorRealizado}
                        onChange={(value) => handleOnChangeForm('valorRealizado', value)}
                        fullWidth
                    />
                </Grid>
            </Grid>
        </React.Fragment>
    );
};
